'use client';

import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

/** Uno slot quartorario con il valore di ciascun livello, o `null` dove non copre. */
export interface PodLayerPoint {
	slot: string;
	gse: number | null;
	reale: number | null;
	stimato: number | null;
	tipico: number | null;
}

export interface PodLayerCoverage {
	gse: number;
	reale: number;
	stimato: number;
	tipico: number;
	/** Slot del giorno civile: 92, 96 o 100 */
	totalSlots: number;
}

interface PodLayersResponse {
	points: PodLayerPoint[];
	coverage: PodLayerCoverage;
}

const EMPTY_COVERAGE: PodLayerCoverage = { gse: 0, reale: 0, stimato: 0, tipico: 0, totalSlots: 96 };
const EMPTY_POINTS: PodLayerPoint[] = [];

export interface UsePodLayersParams {
	orgId?: string;
	meterId?: string | null;
	/** Giorno civile 'YYYY-MM-DD' */
	day?: string | null;
	enabled?: boolean;
}

/**
 * I quattro livelli energetici di un punto di misura su un giorno.
 *
 * Un giorno per volta, non un intervallo: l'esploso serve a guardare da vicino
 * dove ogni fonte copre e quanto la stima si scosta dal reale, e su un mese
 * intero quella differenza non si vedrebbe. Il costo è una riga di
 * `meter_day_curve` per livello — quattro righe, non 384.
 *
 * ADR-006: backed by React Query.
 */
export function usePodLayers({ orgId: explicitOrgId, meterId, day, enabled = true }: UsePodLayersParams) {
	const adminOrgId = useAdminScopeStore((s) => s.configId);
	const orgId = explicitOrgId ?? adminOrgId;
	const active = !!meterId && !!day && enabled && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: ['pod-layers', orgId, meterId, day],
		enabled: active,
		queryFn: () => {
			const params = new URLSearchParams({
				meterId: meterId as string,
				day: day as string,
				orgId: orgId as string,
			});
			return api.get<PodLayersResponse>(`/api/energy/pod-layers?${params.toString()}`);
		},
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	return {
		points: data?.points ?? EMPTY_POINTS,
		coverage: data?.coverage ?? EMPTY_COVERAGE,
		isLoading: active ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento dei livelli'
			: null,
		refetch,
	};
}
